import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import EmbedPreview from '@/components/ui/custom/dataDisplay/EmbedPreview';
import { ApiError } from '@/models/Errors';
import { ProjectMetadata } from '@/models/Project';
import { ClassNameComponent } from '@/utils/ClassNameComponent';
import { AxiosError } from 'axios';
import { PropsWithChildren } from 'react';

type MetadataProps = {
  value?: ProjectMetadata;
  error?: unknown;
};

type Props = {
  className?: Pick<ClassNameComponent, 'base' | 'iframe'>;
  src: string;
  useRedirection?: boolean;
  projectMetadata?: MetadataProps;
};

export default function ProjectEmbedPreview({
  className,
  src,
  useRedirection,
  projectMetadata,
}: Props) {
  const metadataError = projectMetadata?.error;
  const apiError =
    metadataError instanceof AxiosError ? new ApiError(metadataError) : null;
  const isTimeoutError = apiError?.statusCode === 504;

  if (metadataError || !projectMetadata?.value) {
    return (
      <EmbedContainer src={src} displayIframe={isTimeoutError}>
        <EmbedPreview
          useRedirection={useRedirection}
          className={className}
          src={src}
        />
      </EmbedContainer>
    );
  }

  const metadata = projectMetadata.value;
  const embedUrl = metadata.embedUrl || src;
  const isIframeAllowed =
    typeof metadata.isIframeAllowed === 'boolean'
      ? metadata.isIframeAllowed
      : true;

  const shouldUseRedirection = useRedirection || !isIframeAllowed;

  return (
    <EmbedContainer src={src} displayIframe={isIframeAllowed}>
      <EmbedPreview
        useRedirection={shouldUseRedirection}
        className={className}
        src={embedUrl}
      />
    </EmbedContainer>
  );
}

function EmbedContainer({
  children,
  src,
  displayIframe,
}: PropsWithChildren<{ src: string; displayIframe?: boolean }>) {
  return (
    <div className="flex flex-col gap-1.5">
      <ConditionalRenderer condition={displayIframe}>
        {children}
      </ConditionalRenderer>
      <EmbedPreview src={src} useRedirection />
    </div>
  );
}
